import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from "../../images/AE-Hero.svg";
import {
    FiSun,
    FiZap,
    FiHome,
    FiTool,
    FiBattery,
    FiFileText,
    FiTrendingUp,
    FiShield,
    FiWind,
    FiRefreshCw,
    FiClock
} from 'react-icons/fi';

const Services = () => {
    const navigate = useNavigate();
    const [activePortal, setActivePortal] = useState('electrical');
    const [showServices, setShowServices] = useState(false);

    useEffect(() => {
        // Refresh AOS animations when component mounts
        if (typeof window !== 'undefined') {
            const AOS = require('aos');
            AOS.refresh();
        }
    }, []);

    // All services categorized
    const allServices = {
        solar: [
            {
                number: "01",
                title: "Solar Panel Installation",
                description: "Complete solar panel system installation for residential and commercial properties. We handle everything from roof assessment to final connection, using high-efficiency panels and inverters to maximize your energy production and savings.",
                icon: <FiSun className="text-5xl text-green-600" />
            },
            {
                number: "02",
                title: "Solar System Design & Consultation",
                description: "Custom solar solutions tailored to your energy needs and property characteristics. Our experts analyze your consumption patterns, roof orientation, and local climate to design the most efficient solar array for maximum return on investment.",
                icon: <FiTrendingUp className="text-5xl text-green-600" />
            },
            {
                number: "03",
                title: "Solar Battery Storage",
                description: "Advanced energy storage solutions that store excess solar power for use during nighttime or cloudy days. Our battery systems provide energy independence, backup power during outages, and maximize your solar investment.",
                icon: <FiBattery className="text-5xl text-green-600" />
            },
            {
                number: "04",
                title: "Solar Maintenance & Cleaning",
                description: "Regular maintenance and professional cleaning services to keep your solar panels operating at peak efficiency. We monitor system performance, clean panels, and perform necessary repairs to protect your investment.",
                icon: <FiTool className="text-5xl text-green-600" />
            }
        ],
        electrical: [
            {
                number: "01",
                title: "Electrical Installation & Wiring",
                description: "Professional electrical installation services for new constructions, renovations, and upgrades. Our certified electricians handle all wiring, outlets, switches, and lighting installations with strict adherence to safety codes.",
                icon: <FiZap className="text-5xl text-blue-600" />
            },
            {
                number: "02",
                title: "Electrical Panel Upgrades",
                description: "Modern electrical panel upgrades to handle increased power demands and ensure safety. We replace outdated fuse boxes with new circuit breaker panels, providing reliable power distribution for your home or business.",
                icon: <FiHome className="text-5xl text-blue-600" />
            },
            {
                number: "03",
                title: "Emergency Electrical Repairs",
                description: "24/7 emergency electrical repair services for urgent issues like power outages, sparking outlets, or circuit breaker problems. Our rapid response team ensures your safety and restores power quickly.",
                icon: <FiTool className="text-5xl text-blue-600" />
            },
            {
                number: "04",
                title: "Safety Inspections & Code Compliance",
                description: "Comprehensive electrical safety inspections to identify potential hazards and ensure compliance with current NEC codes. We provide detailed reports and necessary upgrades for insurance and property safety.",
                icon: <FiShield className="text-5xl text-blue-600" />
            }
        ],
        generator: [
            {
                number: "01",
                title: "Generator Supply & Installation",
                description: "Professional supply and installation of standby and portable generators for residential and commercial use. We help you choose the right size and type of generator, handle all electrical connections, and ensure seamless automatic transfer switch installation.",
                icon: <FiWind className="text-5xl text-orange-600" />
            },
            {
                number: "02",
                title: "Generator Maintenance & Servicing",
                description: "Comprehensive maintenance programs to keep your generator ready for emergencies. Services include oil changes, filter replacements, battery testing, load bank testing, and full system diagnostics to ensure reliable operation when you need it most.",
                icon: <FiRefreshCw className="text-5xl text-orange-600" />
            },
            {
                number: "03",
                title: "Generator Hire & Rental",
                description: "Flexible generator rental solutions for events, construction sites, or emergency backup needs. We offer a range of portable and industrial generators with delivery, setup, and fuel services included in our rental packages.",
                icon: <FiClock className="text-5xl text-orange-600" />
            },
            {
                number: "04",
                title: "Automatic Transfer Switch Installation",
                description: "Professional installation of automatic transfer switches that instantly switch to generator power during outages. Our systems ensure seamless power transition without manual intervention, protecting your equipment and maintaining operations.",
                icon: <FiFileText className="text-5xl text-orange-600" />
            }
        ]
    };

    const portals = [
        {
            id: 'electrical',
            title: 'Electrical Services',
            description: 'Complete electrical solutions from installation and upgrades to emergency repairs and safety inspections. Fully certified and code-compliant work.',
            icon: <FiZap className="text-6xl text-blue-500 mb-4" />,
            color: 'from-blue-500 to-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            buttonColor: 'bg-blue-600 hover:bg-blue-700'
        },
        {
            id: 'solar',
            title: 'Solar Energy Solutions',
            description: 'Harness the power of the sun with our comprehensive solar installation, design, and maintenance services. Reduce your electricity bills and carbon footprint.',
            icon: <FiSun className="text-6xl text-green-600 mb-4" />,
            color: 'from-green-500 to-green-600',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            buttonColor: 'bg-green-600 hover:bg-green-700'
        },
        {
            id: 'generator',
            title: 'Generator Solutions',
            description: 'Reliable backup power with our generator supply, maintenance, and rental services. Stay powered during outages with our professional generator systems.',
            icon: <FiWind className="text-6xl text-orange-600 mb-4" />,
            color: 'from-orange-500 to-orange-600',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200',
            buttonColor: 'bg-orange-600 hover:bg-orange-700'
        }
    ];

    const portalStyles = {
        solar: {
            hoverGradient: 'hover:to-green-50',
            borderAccent: 'hover:border-green-300',
        },
        electrical: {
            hoverGradient: 'hover:to-blue-50',
            borderAccent: 'hover:border-blue-300',
        },
        generator: {
            hoverGradient: 'hover:to-orange-50',
            borderAccent: 'hover:border-orange-300',
        },
    };

    const handleReadMore = (portalId) => {
        setActivePortal(portalId);
        setShowServices(true);
        // Scroll to services section
        setTimeout(() => {
            document.getElementById('portal-services')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleRequestQuotation = (portalId) => {
        navigate('/quote', { state: { preSelectedService: portalId } });
    };

    const handleBackToPortals = () => {
        setShowServices(false);
    };

    return (
        <>
            <section id="services" className="services py-16 bg-gray-50 relative">
                <div className="absolute top-4 right-6 rounded-full">
                    <img src={Logo} alt="Alternative Energy" className="h-10 w-auto md:h-14 opacity-80" />
                </div>
                <div className="container mx-auto px-4">
                    {/* Section Title */}
                    <div className="section-title text-center mb-12" data-aos="fade-up">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">COMPLETE ENERGY SOLUTIONS</h2>
                        <div className="flex justify-center mb-4">
                            <div className="h-1 w-16 rounded-full" style={{ background: 'var(--accent-color)' }} />
                            <div className="h-1 w-6 rounded-full mx-1 opacity-40" style={{ background: 'var(--accent-color)' }} />
                            <div className="h-1 w-3 rounded-full opacity-20" style={{ background: 'var(--accent-color)' }} />
                        </div>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Professional Electrical, Solar & Generator Services for Homes and Businesses
                        </p>
                    </div>

                    {!showServices ? (
                        /* Portal Selection Cards */
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            {portals.map((portal) => (
                                <div
                                    key={portal.id}
                                    className={`portal-card bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl border ${portal.borderColor}`}
                                    data-aos="fade-up"
                                    data-aos-delay={portal.id === 'electrical' ? '100' : portal.id === 'solar' ? '200' : '300'}
                                >
                                    <div className={`h-2 bg-gradient-to-r ${portal.color}`}></div>
                                    <div className="p-8 text-center">
                                        <div className="flex justify-center mb-4">
                                            {portal.icon}
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-800 mb-3">{portal.title}</h3>
                                        <p className="text-gray-600 mb-6 min-h-[80px]">
                                            {portal.description}
                                        </p>
                                        <div className="flex flex-col gap-3">
                                            <button
                                                onClick={() => handleReadMore(portal.id)}
                                                className={`${portal.buttonColor} text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg`}
                                            >
                                                Read More
                                            </button>
                                            <button
                                                onClick={() => handleRequestQuotation(portal.id)}
                                                className="bg-white border-2 font-semibold py-2.5 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                                                style={{ color: portal.id === 'solar' ? '#16a34a' : portal.id === 'electrical' ? '#2563eb' : '#ea580c', borderColor: portal.id === 'solar' ? '#16a34a' : portal.id === 'electrical' ? '#2563eb' : '#ea580c' }}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Request Quotation
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Services Display for Selected Portal */
                        <div id="portal-services" className="portal-services">
                            {/* Back Button */}
                            <div className="mb-8">
                                <button
                                    onClick={handleBackToPortals}
                                    className="flex items-center text-gray-600 hover:text-gray-800 font-semibold transition-colors duration-300"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Back to Portals
                                </button>
                            </div>

                            {/* Portal Header */}
                            <div className={`text-center mb-12 p-8 rounded-xl ${portals.find(p => p.id === activePortal)?.bgColor}`} data-aos="fade-up">
                                {portals.find(p => p.id === activePortal)?.icon}
                                <h3 className="text-3xl font-bold text-gray-800 mb-3">
                                    {portals.find(p => p.id === activePortal)?.title}
                                </h3>
                                <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
                                    {portals.find(p => p.id === activePortal)?.description}
                                </p>
                                <button
                                    onClick={() => handleRequestQuotation(activePortal)}
                                    className={`inline-flex items-center gap-2 ${portals.find(p => p.id === activePortal)?.buttonColor} text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Request Quotation
                                </button>
                            </div>

                            {/* Service Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {allServices[activePortal].map((service, index) => (
                                    <div
                                        key={index}
                                        className={`service-item relative p-6 border rounded-lg hover:shadow-xl transition-all duration-300 bg-white hover:bg-gradient-to-br hover:from-white ${portalStyles[activePortal].hoverGradient} ${portalStyles[activePortal].borderAccent} transform hover:-translate-y-1`}
                                        data-aos="fade-up"
                                        data-aos-delay={index * 100}
                                    >
                                        <span className="number absolute top-4 right-4 text-6xl font-bold text-gray-100 z-0">
                                            {service.number}
                                        </span>
                                        <div className="service-item-icon mb-6 relative z-10">
                                            {service.icon}
                                        </div>
                                        <div className="service-item-content relative z-10">
                                            <h3 className="service-heading text-xl font-semibold mb-3 text-gray-800">
                                                {service.title}
                                            </h3>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                {service.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Additional Info Section */}
                            <div className="mt-16 text-center" data-aos="fade-up">
                                <div className="inline-flex items-center justify-center space-x-4 mb-6">
                                    <div className={`w-12 h-1 ${activePortal === 'solar' ? 'bg-green-600' : activePortal === 'electrical' ? 'bg-blue-600' : 'bg-orange-600'}`}></div>
                                    <span className={`font-semibold ${activePortal === 'solar' ? 'text-green-600' : activePortal === 'electrical' ? 'text-blue-600' : 'text-orange-600'}`}>
                                        Why Choose Us
                                    </span>
                                    <div className={`w-12 h-1 ${activePortal === 'solar' ? 'bg-green-600' : activePortal === 'electrical' ? 'bg-blue-600' : 'bg-orange-600'}`}></div>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {activePortal === 'solar' && 'Complete Solar Solutions'}
                                    {activePortal === 'electrical' && 'Professional Electrical Services'}
                                    {activePortal === 'generator' && 'Reliable Generator Systems'}
                                </h3>
                                <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                                    <div className="bg-white p-6 rounded-lg shadow-sm">
                                        <h4 className={`font-semibold mb-2 ${activePortal === 'solar' ? 'text-green-700' : activePortal === 'electrical' ? 'text-blue-700' : 'text-orange-700'}`}>
                                            {activePortal === 'solar' && 'Certified Solar Experts'}
                                            {activePortal === 'electrical' && 'Certified Electricians'}
                                            {activePortal === 'generator' && 'Certified Generator Technicians'}
                                        </h4>
                                        <p className="text-gray-600 text-sm">
                                            {activePortal === 'solar' && 'Our NABCEP-certified solar technicians are fully insured and trained in the latest panel and inverter technologies.'}
                                            {activePortal === 'electrical' && 'Our team is fully licensed, insured, and trained in the latest technologies and safety standards.'}
                                            {activePortal === 'generator' && 'Our technicians are factory-trained and certified to install, service, and repair all major generator brands.'}
                                        </p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm">
                                        <h4 className={`font-semibold mb-2 ${activePortal === 'solar' ? 'text-green-700' : activePortal === 'electrical' ? 'text-blue-700' : 'text-orange-700'}`}>
                                            {activePortal === 'solar' && '25-Year Performance Warranty'}
                                            {activePortal === 'electrical' && 'Quality Guarantee'}
                                            {activePortal === 'generator' && 'Reliability Guarantee'}
                                        </h4>
                                        <p className="text-gray-600 text-sm">
                                            {activePortal === 'solar' && 'We stand behind our installations with 25-year solar panel performance warranties and full workmanship guarantees.'}
                                            {activePortal === 'electrical' && 'We use premium equipment and materials, backed by comprehensive warranties and guarantees.'}
                                            {activePortal === 'generator' && 'Every generator we install or service is backed by a reliability guarantee — your power will be there when you need it.'}
                                        </p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm">
                                        <h4 className={`font-semibold mb-2 ${activePortal === 'solar' ? 'text-green-700' : activePortal === 'electrical' ? 'text-blue-700' : 'text-orange-700'}`}>
                                            {activePortal === 'solar' && 'Ongoing Monitoring & Support'}
                                            {activePortal === 'electrical' && '24/7 Support'}
                                            {activePortal === 'generator' && '24/7 Emergency Support'}
                                        </h4>
                                        <p className="text-gray-600 text-sm">
                                            {activePortal === 'solar' && 'We provide remote system monitoring and proactive maintenance to keep your solar investment performing at its best.'}
                                            {activePortal === 'electrical' && 'Round-the-clock customer support and emergency services when you need them most.'}
                                            {activePortal === 'generator' && 'Round-the-clock emergency callout for generator failures, fuel supply issues, and urgent repairs — we keep you powered.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default Services;